<!-- Copyright 2025, Common Good Learning Tools LLC -->
<template>
	<div v-if="local_item">
		<div class="k-asset-publish-status-outer">
			<div style="flex:0 1 100%">
				<div class="k-lesson-title d-flex align-center mt-1">
					<v-spacer/>
					<b>HQIM Publishing Status:</b>
					<v-spacer/>
					
					<v-tooltip bottom>
						<template v-slot:activator="{ on }">
							<v-icon v-on="on" class="mr-2" ref="button" small @click="log_showing = !log_showing">far fa-rectangle-history</v-icon>
						</template>
						<span>View Log</span>
					</v-tooltip>
				</div>

				<div class="mt-1 mb-1 d-flex flex-wrap align-center" style="justify-content: center;">
					<div v-for="step in steps" :class="step.step == 'spacer' ? step.class : 'k-asset-publish-step-placeholder'">
						<v-icon v-if="step.current" color="#000">fas fa-down</v-icon>
					</div>
				</div>

				<div class="mb-3 d-flex flex-wrap align-center justify-center mb-4">
					<div v-for="(step, index) in steps" :key="step.step + '_' + index">
						<div v-if="step.step == 'spacer'" class="k-asset-publish-line"></div>

						<v-tooltip v-else :disabled="step.tooltip == ''" bottom>
							<template v-slot:activator="{on}">
								<div v-on="on" class="k-asset-publish-step" :class="step.class" @click="set_publish_status(step.new_status)">
									<v-icon>fas {{ step.icon }}</v-icon>
									<div v-html="step.html"></div>
								</div>
							</template>
							
							<div class="text-center" v-html="step.tooltip"></div>
						</v-tooltip>
					</div>
				</div>

				<div class="d-flex flex-wrap align-center justify-center mb-4">
					<v-tooltip v-for="step_btn in step_btns" :key="step_btn.key" :disabled="step_btn.tooltip == ''" bottom>
						<template v-slot:activator="{on}">
							<v-btn v-on="on" v-if="step_btn.visible" class="k-tight-btn mx-2" :color="step_btn.btn_color + ' darken-3'" outlined dark @click="set_publish_status(step_btn.new_status)">
								<v-icon small class="mr-2">fas {{step_btn.icon}}</v-icon>
								{{step_btn.btn_text}}
							</v-btn>
						</template>

						<div class="text-center" v-html="step_btn.tooltip"></div>
					</v-tooltip>
				</div>

				<div v-if="step_btns[1].msg" class="d-flex mb-4">
					<v-spacer/>
					<div :class="step_btns[1].btn_color" class="k-asset-publish-msg lighten-5" v-html="step_btns[1].msg"></div>
					<v-spacer/>
				</div>

				<div class="k-asset-publish-status-card-people d-flex my-6 justify-center">
					<div><b style="background-color:#eee; padding:2px 4px; border-radius:4px; color:#444">{{asset_type_noun}} Submitter:</b> {{creator_name || 'Unknown'}}</div>
					
					<div class="ml-4"><b style="background-color:#eee; padding:2px 4px; border-radius:4px; color:#444">Reviewer:</b> {{reviewers.join(', ')}}</div>
					
					<v-btn v-if="publish_roles.includes('publisher')" color="#444" class="k-tight-btn ml-3" x-small text @click="find_reviewer_email">
						<v-icon class="mr-1" x-small>fas fa-user-magnifying-glass</v-icon>
						Assign new reviewer
					</v-btn>
				</div>

				<!-- by using v-if here, we don't load the AssetReview component until we've retrieved the asset's review from the server -->
				<AssetReview
					v-if="asset_review_data"
					:asset_review_data="asset_review_data"
					@review_saved="review_saved"
				/>
			</div>

			<div v-if="log_showing" class="ml-2">
				<AssetLog ref="log" :log="log"/>		
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import AssetLog from './AssetLog.vue'
import AssetReview from './AssetReview.vue'

export default {
	components: {AssetLog, AssetReview},

	props: {
		item: {type: Object, required: true},
	},

	data() {
		return {
			local_item: null,
			publish_roles: [],
			creator_name: '',
			creator_email: '',
			current_publish_status: {},
			asset_review_data: null,
			log: [],
			log_showing: false,
		}
	},

	computed: {
		...mapState(['site_config', 'user_info']),

		// the asset_type for the item: lesson, activity, or resource
		asset_type() {
			if (!empty(this.item.lesson_title)) return 'lesson'
			if (this.item.type == 'sparkl') return 'activity'
			return 'resource'
		},

		user_is_admin() {
			return this.user_info.system_role == 'admin' || this.user_info.admin_rights.includes('su')
		},

		alignment_required() {
			return this.site_config.asset_submission_requires_alignment
		},

		submit() {
			let review = true
			let alignments = true

			if (this.site_config.asset_submission_requires_review) {
				if (Array.isArray(this.asset_review_data)){
					review = false
				} else if(this.asset_review_data.sections) {
					Object.values(this.asset_review_data.sections).every(section => {
						if (section.score === null) {
							review = false
							return false
						}

						return true
					})
				}
			}
			if (this.site_config.asset_submission_requires_alignment && !this.local_item.standards.length) alignments = false

			return {enabled: review && alignments, review, alignments}
		},

		asset_id() {
			return this.asset_type == 'lesson' ? this.item.lesson_id : this.item.resource_id
		},

		asset_type_noun() {
			if (this.asset_type === 'lesson') return 'Lesson'
			if (this.item.type == 'sparkl') return this.site_config.sparkl_app_name + ' Activity'
			return 'Resource'
		},

		reviewers() {
			let reviewers = []
			// TODO: return list of reviewers for display

			this.log.forEach(x => {
				if (x.hasOwnProperty('assigned_user_name')) reviewers.push(x.assigned_user_name)
			})

			if (!reviewers.length) reviewers.push('No reviewers assigned')
			// default string
			return reviewers.reverse()
		},

		steps() {
			return ['unsubmitted', 'spacer', 'submitted', 'spacer', 'under_review', 'spacer', 'ready_for_publication', 'spacer', 'published'].map(this.step_bind)
		},

		step_btns() {
			return ['prev', 'next'].map(this.step_btn_bind)
		},
	},

	mounted() {
		vapp.asset_publish_status_component = this

		// retrieve the full item, along with the publish_roles for this user and the asset_review_data
		let payload = {
			user_id: this.user_info.user_id,
			asset_type: this.asset_type,
			asset_id: this.asset_id,
		}

		U.ajax('get_unpublished_assets', payload, result => {
			if (!result.status == 'ok') {
				console.error('something went wrong: ', result)
			} else if (result.assets){
				let rv = result.assets[0];

				// note that we don't (currently at least) instantiate an Asset_Review object, because we don't currently know the template at this point; we let this happen in AssetReview.vue
				this.asset_review_data = rv.asset_review_data

				this.publish_roles = rv.roles;
				this.creator_name = rv.creator_name;
				this.creator_email = rv.creator_email;
				this.set_current_publish_status(rv.publish_status)

				this.local_item = this.asset_type === 'lesson' ? new Lesson(rv) : new Resource(rv)
				this.load_log()
			}
		})
	},

	methods: {
		// determine the parameters needed to render each "step bubble" in the user interface
		// this function, along with step_btn_bind below, effectively defines much of the business logic around the publishing workflow
		step_bind(step) {
			if (step == 'spacer') return {step, class: 'k-asset-publish-line-placeholder'}
			let key = this.current_publish_status.key
			if (empty(key) || key == 'returned_to_author') key = 'unsubmitted'

			let steps = ['unsubmitted', 'submitted', 'under_review', 'ready_for_publication', 'published']
			let ki = steps.indexOf(key)
			let si = steps.indexOf(step)

			let o = {
				step,
				class: `k-asset-publish-step-${step}`,
				// this is set to true for the current step
				current: false,
				// html and tooltip to show for the step
				html: '',
				tooltip: '',
				// if clicked, new_status.key is the new publish_status; new_status.text is what we'll inform the user
				// if new_status is 'unclickable', set_publish_status won't do anything
				new_status: {key: null, text: ''},
			}

			let where
			// if this step is where we currently are in the publish process 'queue'
			if (ki == si) {
				where = 'current'
				o.class += ' k-asset-publish-current-step'
				o.current = true

			// else if this step is earlier than where we currently are
			} else if (si < ki) {
				where = 'earlier'
				o.class += ' k-asset-publish-earlier-step'
				o.current = false

			// else if this step is later than where we currently are
			} else {
				where = 'later'
				o.class += ' k-asset-publish-later-step'
				o.current = false
			}

			switch(step) {
				case 'unsubmitted':
					// IF STEP BEING RENDERED IS 'unsubmitted'
					o.icon = 'fa-file-user'
					// if the item is currently in the 'returned_to_author' state...
					if (this.current_publish_status.key == 'returned_to_author') {
						o.html = '<div>Returned<br>to Author</div>'

						// if the user is the author, clicking this button withdraws it for consideration
						if (this.publish_roles.includes('author')) {
							o.tooltip = 'Withdraw from<br>publication consideration'
							o.new_status = {key: null, text: 'Withdrawn for publication consideration'}

						// if the user has reviewer or publisher rights, clicking this button rejects it
						} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
							o.tooltip = 'Reject for<br>publication'
							o.new_status = {key: null, text: 'Rejected for Publication'}

						} else {
							// I don't think we can get to here, but if we do, don't do anything if the step is clicked
							o.new_status = 'unclickable'
						}

					// else if the asset is (currently) not in the publish process at all (it could conceivably have been previously submitted and then rejected), the step isn't clickable at all
					} else if (empty(this.current_publish_status.key)) {
						o.html = '<div>Not<br>Submitted</div>'
						o.new_status = 'unclickable'

					// else the asset is somewhere else in the publish process, so the label for this step is 'Not Submitted', and...
					} else {
						o.html = '<div>Not<br>Submitted</div>'

						// if the user is the author, clicking this button withdraws it for consideration
						if (this.publish_roles.includes('author')) {
							o.tooltip = 'Withdraw from<br>publication consideration'
							o.new_status = {key: null, text: 'Withdrawn for publication consideration'}

						// if the user has reviewer or publisher rights, clicking this button returns it to the author
						} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
							o.tooltip = 'Return to author<br>for revision'
							o.new_status = {key: 'returned_to_author', text: 'Returned to Author'}

						} else {
							// I don't think we can get to here, but if we do, don't do anything if the step is clicked
							o.new_status = 'unclickable'
						}
					}

					break;
				case 'submitted':
					// IF STEP BEING RENDERED IS 'submitted'
					o.icon = 'fa-file-export'
					o.html = '<div>Submitted<br> </div>'	// <br>for<br>Review

					// if this is the original author and the asset is not currently submitted,
					if (this.publish_roles.includes('author') && ['unsubmitted', 'returned_to_author'].includes(this.current_publish_status.key) && this.submit.enabled) {
						// then clicking this step should submit it
						o.tooltip = 'Submit for<br>publication'
						o.new_status = {key: 'submitted', text: 'Submitted for Publication'}

					// else the button is unclickable: there's (currently at least) no point in moving an asset from "under review" to "submitted"
					} else {
						o.new_status = 'unclickable'
					}

					break;
				case 'under_review':
					// IF STEP BEING RENDERED IS 'under_review'
					o.icon = 'fa-file-magnifying-glass'
					o.html = '<div>Under<br>Review</div>'

					// if we're currently on this step, it's unclickable
					if (where == 'current') {
						o.new_status = 'unclickable'

					// else if the user has reviewer or publisher rights, clicking this button moves it to this step
					} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						// but message is different depending on the direction being moved
						if (where == 'later') {
							o.tooltip = 'Begin review'
							o.new_status = {key: 'under_review', text: 'Under Review'}
						} else {
							o.tooltip = 'Return for<br>further review'
							o.new_status = {key: 'under_review', text: 'Returned for Further Review'}
						}

					// else the user (presumably the author, who doesn't have reviewer/publisher rights) can't click this step
					} else {
						o.new_status = 'unclickable'
					}

					break;
				case 'ready_for_publication':
					// IF STEP BEING RENDERED IS 'ready_for_publication'
					o.icon = 'fa-file-check'
					o.html = '<div>Approved for<br>Publication</div>'

					// if we're currently on this step, it's unclickable
					if (where == 'current') {
						o.new_status = 'unclickable'

					// else if the user has reviewer or publisher rights, clicking this button moves it to this step
					} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						// but message is different depending on the direction being moved
						if (where == 'later') {
							o.tooltip = 'Approve for<br>publication'
							o.new_status = {key: 'ready_for_publication', text: 'Approved for Publication'}
						} else {
							o.tooltip = 'Set status to<br>“Approved for Publication”'
							o.new_status = {key: 'ready_for_publication', text: 'Approved for Publication'}
						}

					// else the user (presumably the author, who doesn't have reviewer/publisher rights) can't click this step
					} else {
						o.new_status = 'unclickable'
					}
					break;
				case 'published':
					// IF STEP BEING RENDERED IS 'published'
					o.icon = 'fa-file-certificate'
					o.html = '<div>Published<br> </div>'

					// if we're currently on this step, it's unclickable
					if (where == 'current') {
						o.new_status = 'unclickable'

					// else if the user has reviewer or publisher rights, clicking this button moves it to this step
					} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						// (there is no step later than this one)
						o.tooltip = 'Publish!'
						o.new_status = {key: 'published', text: 'Published'}

					// else the user (presumably the author, who doesn't have reviewer/publisher rights) can't click this step
					} else {
						o.new_status = 'unclickable'
					}
					break;
			}

			// if new_status is unclickable, set appropriate class
			if (o.new_status == 'unclickable') {
				o.class += ' k-asset-publish-current-unclickable'
			}

			return o
		},

		// determine the parameters needed to render the "previous" and "next" buttons that appear below the "step bubbles"
		// this function, along with step_bind above, effectively defines much of the business logic around the publishing workflow
		step_btn_bind(direction) {
			let o = {
				// note that if o.visible is false, the button won't be clickable, so it doesn't matter what the other params are
				visible: true,
				tooltip: '',	// not currently used
				btn_text: '',
				btn_color: 'black',
				// new_status.key is the new publish_status; new_status.text is what we'll inform the user; 
				// if new_status.confirm is non-empty, we'll ask the user to confirm before taking the action
				new_status: {key: null, text: '', confirm: ''},
				icon: '',
				// msg to show the user; only set for next
				msg: '',
			}

			let key = this.current_publish_status.key
			if (empty(key) || key == 'returned_to_author') key = 'unsubmitted'

			// IF DIRECTION IS 'prev' (BACK IN THE PUBLISHING PROCESS)
			if (direction == 'prev') {
				o.key = 'prev'
				o.icon = 'fa-left'
				// IF CURRENT STEP IS 'unsubmitted'
				if (key == 'unsubmitted') {
					if (this.current_publish_status.key == 'unsubmitted') {
						o.visible = false

					} else {	// returned_to_author
						// if item has already been returned to the author, prev is 'reject' for reviewer/publisher
						if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
							o.tooltip = 'Reject for<br>publication'
							o.btn_text = 'Reject for publication'
							o.btn_color = 'black'
							o.new_status = {key: null, text: 'Rejected for Publication'}
							o.new_status.confirm = `Are you sure you want to reject this ${this.asset_type_noun} for publication consideration?`

						// if the user is the author, clicking the prev button withdraws it for consideration
						} else if (this.publish_roles.includes('author')) {
							o.tooltip = 'Withdraw from<br>publication consideration'
							o.btn_text = 'Withdraw from publication consideration'
							o.btn_color = 'black'
							o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
							o.new_status.confirm = `Are you sure you want to withdraw this ${this.asset_type_noun} for publication consideration?`

						} else {
							// I don't think we can get to here, but if we do, button is not visible
							o.visible = false
						}
					}

				// IF CURRENT STEP IS 'submitted'
				} else if (key == 'submitted') {
					// if the user is the author, clicking the prev button withdraws it for consideration
					if (this.publish_roles.includes('author')) {
						o.tooltip = 'Withdraw from<br>publication consideration'
						o.btn_text = 'Withdraw from publication consideration'
						o.btn_color = 'black'
						o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
						o.new_status.confirm = `Are you sure you want to withdraw this ${this.asset_type_noun} for publication consideration?`

					// if the user has reviewer or publisher rights, clicking the prev button returns it to the author
					} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Return to author<br>for revision'
						o.btn_text = 'Return to author for revision'
						o.btn_color = 'deep-orange'
						o.new_status = {key: 'returned_to_author', text: 'Returned to Author'}

					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}

				// IF CURRENT STEP IS 'under_review'
				} else if (key == 'under_review') {
					// if the user has reviewer or publisher rights, clicking the prev button returns it to the author
					if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Return to author<br>for revision'
						o.btn_text = 'Return to author for revision'
						o.btn_color = 'deep-orange'
						o.new_status = {key: 'returned_to_author', text: 'Returned to Author'}

					// if the user is the author, clicking the prev button withdraws it for consideration
					} else if (this.publish_roles.includes('author')) {
						o.tooltip = 'Withdraw from<br>publication consideration'
						o.btn_text = 'Withdraw from publication consideration'
						o.btn_color = 'black'
						o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
						o.new_status.confirm = `Are you sure you want to withdraw this ${this.asset_type_noun} for publication consideration?`

					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}

				// IF CURRENT STEP IS 'ready_for_publication'
				} else if (key == 'ready_for_publication') {
					// if the user has reviewer or publisher rights, clicking the prev button returns to the reviewer
					if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Return for<br>further review'
						o.btn_text = 'Return for further review'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Returned for Further Review'}

					// if the user is the author, clicking the prev button withdraws it for consideration
					} else if (this.publish_roles.includes('author')) {
						o.tooltip = 'Withdraw from<br>publication consideration'
						o.btn_text = 'Withdraw from publication consideration'
						o.btn_color = 'black'
						o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
						o.new_status.confirm = `Are you sure you want to withdraw this ${this.asset_type_noun} for publication consideration?`

					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}

				// IF CURRENT STEP IS 'published'
				} else if (key == 'published') {
					// if the user has reviewer or publisher rights, clicking the prev button returns to the reviewer
					if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Unpublish'
						o.btn_text = 'Unpublish'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Unpublished'}

					// if the user is the author, clicking the prev button withdraws it for consideration
					} else if (this.publish_roles.includes('author')) {
						o.tooltip = 'Withdraw from<br>publication consideration'
						o.btn_text = 'Withdraw from publication consideration'
						o.btn_color = 'black'
						o.new_status = {key: null, text: 'Withdrawn for publication consideration'}
						o.new_status.confirm = `Are you sure you want to withdraw this ${this.asset_type_noun} for publication consideration?`

					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}
				}

			// IF DIRECTION IS 'next' (FORWARD IN THE PUBLISHING PROCESS)
			} else {
				o.key = 'next'
				o.icon = 'fa-right'
				// IF CURRENT STEP IS 'unsubmitted'
				if (key == 'unsubmitted') {
					// if the user is the author, clicking the next button submits for publication
					if (this.publish_roles.includes('author')) {
						o.tooltip = 'Submit for<br>publication'
						o.btn_text = 'Submit for publication'
						o.btn_color = 'pink'
						
						if (!this.submit.enabled) {
							o.visible = false
							o.msg = !this.submit.alignments ? `Align ${this.asset_type_noun} to standards` : ''

							if (!this.submit.review) {
								o.msg += o.msg.length ? ` and complete the 'HQIM Review' below` : `Complete the 'HQIM Review' below`
							}

							o.msg += ` to enable submission`
						} else {
							o.new_status = this.submit_enabled ? {key: 'submitted', text: 'Submitted for Publication'} : 'unclickable'
							o.msg = `Click the <i class="fas fa-right"></i> button above to submit this ${this.asset_type_noun} for publication`
						}
					// if the user has reviewer or publisher rights, clicking the next button puts into review mode
					} else if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Review for<br>publication'
						o.btn_text = 'Review for publication'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Under Review'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to start the process of reviewing this ${this.asset_type_noun} for publication`

					} else {
						// I don't think we can get to here, but if we do, button is not visible
						o.visible = false
					}

				// IF CURRENT STEP IS 'submitted'
				} else if (key == 'submitted') {
					// for reviewer/publisher, clicking the next button puts into review mode
					if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Begin Review'
						o.btn_text = 'Begin Review'
						o.btn_color = 'purple'
						o.new_status = {key: 'under_review', text: 'Under Review'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to start the process of reviewing this ${this.asset_type_noun} for publication`

					} else {
						// else next is not clickable
						o.visible = false
						o.btn_color = 'pink'
						o.msg = `This ${this.asset_type_noun} has been submitted for publication`
					}

				// IF CURRENT STEP IS 'under_review'
				} else if (key == 'under_review') {
					// for reviewer/publisher, clicking the next button moves to ready_for_publication
					if (this.publish_roles.includes('reviewer') || this.publish_roles.includes('publisher')) {
						o.tooltip = 'Approve for<br>publication'
						o.btn_text = 'Approve for publication'
						o.btn_color = 'blue'
						o.new_status = {key: 'ready_for_publication', text: 'Approved for Publication'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to approve this ${this.asset_type_noun} for publication`

					} else {
						// else next is not clickable
						o.visible = false
						o.btn_color = 'purple'
						o.msg = `This ${this.asset_type_noun} is currently under review`
					}
					
				// IF CURRENT STEP IS 'ready_for_publication'
				} else if (key == 'ready_for_publication') {
					// for publisher (NOT reviewer), clicking the next button publishes the item
					if (this.publish_roles.includes('publisher')) {
						o.tooltip = 'Publish!'
						o.btn_text = 'Publish'
						o.btn_color = 'green'
						o.new_status = {key: 'published', text: 'Published'}
						o.msg = `Click the <i class="fas fa-right"></i> button above to publish this ${this.asset_type_noun}`

					} else {
						// else next is not clickable
						o.visible = false
						o.btn_color = 'blue'
						o.msg = `This ${this.asset_type_noun} is currently approved for publication, but has not yet been published`
					}

				// IF CURRENT STEP IS 'published'
				} else if (key == 'published') {
					o.visible = false
					o.btn_color = 'green'
					o.msg = `This ${this.asset_type_noun} is published`
					if (this.publish_roles.includes('publisher')) {
						o.msg += ` Click the <i class="fas fa-left"></i> button above to unpublish it`
					}
				}
			}

			return o
		},

		async set_publish_status(new_status) {
			// if new_status is 'unclickable', do nothing
			if (new_status == 'unclickable') return

			// if new_status.confirm is non-empty, we'll ask the user to confirm before taking the action
			if (!empty(new_status.confirm)) {
				try {
					await this.$confirm({
						title: 'Are you sure?',
						text: new_status.confirm,
					})
				} catch(e) { return }
			}

			let today = new Date().toJSON().slice(0, 19).replace('T', ' ')
			if (this.asset_type === 'lesson') {
				let lesson_data = object_copy(this.local_item)
				lesson_data.publish_status = new_status.key
				lesson_data.status_date = today
				if (new_status.key === 'submitted') {
					lesson_data.submitted_date = today
				}
				if (new_status.key === 'published') {
					// note that agency_sanctioned must be sent 
					lesson_data.agency_sanctioned = true
					lesson_data.published_date = today
				} else {
					lesson_data.agency_sanctioned = false
				}
				let lesson_class = lesson_data.lesson_id === lesson_data.template_id ? 'template' : 'teacher'
				let payload = {
					lesson_data,
					lesson_class
				}
				
				let log_data = {
					'user_id': this.user_info.user_id,
					'asset_id': this.local_item.lesson_id,
					'asset_type': 'lesson',
					'asset_creator_id': this.local_item.creator_user_id,
					'action': 'status',
					'from': this.current_publish_status.key,
					'to': new_status.key
				}

				this.$store.dispatch('save_lesson', payload)
					.then(() => {
						this.$store.dispatch('save_asset_log', log_data)
							.then(() => {
								this.local_item.publish_status = new_status.key
								this.local_item.status_date = today
								this.item.publish_status = new_status.key
								this.item.status_date = today
								this.set_current_publish_status(new_status.key)
								if (lesson_data.submitted_date) {
									this.item.submitted_date = lesson_data.submitted_date
									this.local_item.submitted_date = lesson_data.submitted_date
								}
								this.load_log()
								this.$inform({text:'Status updated to: ' + new_status.text})
								this.$emit('status_updated', this.item, this.asset_type)
							})
					})
			} else {
				let resource = new Resource(object_copy(this.local_item))
				resource.publish_status = new_status.key
				resource.status_date = today
				if (new_status.key === 'submitted') {
					resource.submitted_date = today
				}
				if (new_status.key === 'published') {
					// note that agency_sanctioned must be sent 
					resource.agency_sanctioned = true
					resource.published_date = today
				} else {
					resource.agency_sanctioned = false
				}

				let log_data = {
					'user_id': this.user_info.user_id,
					'asset_id': this.local_item.resource_id,
					'asset_type': 'resource',
					'asset_creator_id': this.local_item.creator,
					'action': 'status',
					'from': this.current_publish_status.key,
					'to': new_status.key
				}

				this.$store.dispatch('save_resource', {resource})
					.then(() => {
						this.$store.dispatch('save_asset_log', log_data)
							.then(() => {
								this.local_item.publish_status = new_status.key
								this.local_item.status_date = today
								this.item.publish_status = new_status.key
								this.item.status_date = today
								this.set_current_publish_status(new_status.key)
								if (resource.submitted_date) {
									this.item.submitted_date = resource.submitted_date
									this.local_item.submitted_date = resource.submitted_date
								}
								this.load_log()
								this.$inform({text:'Status updated to: ' + new_status.text})
								this.$emit('status_updated', this.item, this.asset_type)
							})
					})
			}
		},

		set_current_publish_status(publish_status) {
			this.current_publish_status = object_copy(window.get_publish_status_data(publish_status))
		},

		find_reviewer_email() {
			this.$prompt({
				title: 'Assign Reviewer',
				text: 'Enter user email:',
				promptType: 'autocomplete',
				serviceName: 'email_search',
				acceptText: 'Assign',
			}).then(email => {
				// return value should be e.g. 'pepper@gmail.com (Pepper Williams)'; extract the email
				email = $.trim(email).toLowerCase().replace(/^(\S+).*/, '$1')

				if (empty(email) || email.indexOf('@') == -1) {
					this.$alert('Please enter a valid user email.')
						.then(x => { this.find_reviewer_email() })
				} else {	
					this.validate_email(email)
				}
			}).catch(n => { console.error(n) })
		},

		validate_email(email) {
			let payload = {
				user_id: this.user_info.user_id,
				email: email,
				get_rights: true,
			}
			U.loading_start()
			U.ajax('admin_validate_email', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.error('Error in admin ajax call'); vapp.ping(); return;
				}

				if (empty(result.user_id)) {
					this.$alert('We couldn’t find that email address in the system.').then(x=>{this.find_reviewer_email()})
				} else {
					this.assign_reviewer(result)
				}
			});
		},

		assign_reviewer(user) {
			let rights_arr = user.admin_rights ?? []
			rights_arr.push('asset_reviewer.' + this.asset_type + '_id.' + this.asset_id)

			let payload = {
				user_id: this.user_info.user_id,
				user_identifier: user.user_id,
				rights_arr,
			}

			U.loading_start()
			U.ajax('admin_update_admin_rights', payload, result => {
				U.loading_stop()
				if (result.status != 'ok') {
					console.error('Error in admin ajax call'); vapp.ping(); return;
				} else {
					let asset_creator_id = this.asset_type == 'lesson' ? this.local_item.creator_user_id : this.local_item.creator
					let log_data = {
						user_id: this.user_info.user_id,
						asset_id: this.asset_id,
						asset_type: this.asset_type,
						asset_creator_id,
						action: 'assignment',
						assigned_user_id: user.user_id
					}
					
					this.$store.dispatch('save_asset_log', log_data)
						.then(() => {
							this.load_log()
							this.$inform(user.first_name + ' ' + user.last_name + ' assigned to review this asset.')
						})
				}
			});
		},

		review_saved(updated_review) {
			// if/when the review is saved, update it in this.asset_review.
			// note that the review is saved separately (in a different DB table) than the resource/lesson
			this.asset_review_data = new Asset_Review(updated_review)
		},

		async load_log() {
			let payload = {
				asset_id: this.asset_id, 
				user_id: this.user_info.user_id
			}

			await U.ajax('get_asset_log', payload, result => {
				if (!result.status == 'ok') {
					console.error('something went wrong: ', result)
				} else {
					// sort with latest entries first
					this.log = result.log.sort((a,b) => {
						if (a.created_at < b.created_at) return 1
						if (a.created_at > b.created_at) return -1
						return 0
					})
				}
			})
		},
	}
}
</script>

<style lang="scss">
.k-asset-publish-status-outer {
	display: flex;
	padding: 8px;
	border-radius: 12px;
	background-color: #f1f1f1;
	box-shadow: 0 1px 3px 1px rgba(64, 64, 64, 0.5);
}

.k-asset-publish-msg {
	text-align: center;
	padding: 4px 8px;
	border-radius: 8px;
}

.k-asset-publish-step {
	font-size: 14px;
	line-height: 16px;
	text-align: center;
	flex: 0 0 108px;
	height: 108px;
	width: 108px;
	border: 1px solid #000;
	border-radius: 108px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	.v-icon {
		margin-bottom: 4px;
		margin-top: -12px;
		font-size: 24px;
	}
}

.k-asset-publish-step-placeholder {
	text-align: center;
	flex: 0 0 108px;
}

.k-asset-publish-line {
	flex: 0 0 24px;
	width: 24px;
	border-bottom: 4px solid #000;
}

.k-asset-publish-line-placeholder {
	flex: 0 0 24px;
}

.k-asset-publish-earlier-step {
	color: #ccc;
	.v-icon { color: #ccc; }
}
.k-asset-publish-current-step {
	color: #fff;
	font-weight: bold;
	border-width: 4px;
	.v-icon { color: #fff; }
}
.k-asset-publish-later-step {
	color: #000;
	.v-icon { color: #666; }
}

.k-asset-publish-current-unclickable {
	cursor: default;
}

.k-asset-publish-step-unsubmitted { background-color: $v-deep-orange-darken-3; }
.k-asset-publish-step-unsubmitted.k-asset-publish-later-step { background-color: $v-deep-orange-lighten-4; }

.k-asset-publish-step-submitted { background-color: $v-pink-darken-3; }
.k-asset-publish-step-submitted.k-asset-publish-later-step { background-color: $v-pink-lighten-4; }

.k-asset-publish-step-under_review { background-color: $v-purple-darken-3; }
.k-asset-publish-step-under_review.k-asset-publish-later-step { background-color: $v-purple-lighten-4; }

.k-asset-publish-step-ready_for_publication { background-color: $v-blue-darken-3; }
.k-asset-publish-step-ready_for_publication.k-asset-publish-later-step { background-color: $v-blue-lighten-4; }

.k-asset-publish-step-published { background-color: $v-green-darken-3; }
.k-asset-publish-step-published.k-asset-publish-later-step { background-color: $v-green-lighten-4; }

.k-asset-publish-status-card-people {
	text-align: center;
	font-size: 16px;
	line-height: 1.3em;
}
</style>